import { useState, useEffect, useCallback } from 'react';

const RECORDS_PER_PAGE = 10;

export const useSecureMedia = (records, externalPage = null) => {
    const [mediaBlobUrls, setMediaBlobUrls] = useState({});
    const [internalPage, setInternalPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);

    const currentPage = externalPage !== null ? externalPage : internalPage;
    const setCurrentPage = externalPage !== null ? (() => {}) : setInternalPage;

    const fetchSecureMedia = useCallback(async (url) => {
        if (!url) return null;
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            const token = user?.token;
            if (!token) return null;

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                console.error('Failed to fetch media:', response.status);
                return null;
            }

            const blob = await response.blob();
            return URL.createObjectURL(blob);
        } catch (error) {
            console.error('Error fetching secure media:', error);
            return null;
        }
    }, []);

    useEffect(() => {
        let isMounted = true;

        const loadMedia = async () => {
            if (!records || records.length === 0) {
                if (isMounted) {
                    setMediaBlobUrls({});
                }
                return;
            }

            setIsLoading(true);
            const currentRecordIds = new Set(records.map(r => r.id));
            const newBlobUrls = {};
            const promises = [];

            records.forEach(record => {
                if (record.id) {
                    promises.push(
                        (async () => {
                            const photoBlobUrl = record.photoUrl ? await fetchSecureMedia(record.photoUrl) : null;
                            const videoBlobUrl = record.videoUrl ? await fetchSecureMedia(record.videoUrl) : null;
                            if (photoBlobUrl || videoBlobUrl) {
                                newBlobUrls[record.id] = {
                                    photoUrl: photoBlobUrl,
                                    videoUrl: videoBlobUrl
                                };
                            }
                        })()
                    );
                }
            });

            await Promise.all(promises);

            if (isMounted) {
                setMediaBlobUrls(prev => {
                    // Revoke blob URLs for records that are no longer in filtered results
                    Object.keys(prev).forEach(id => {
                        if (!currentRecordIds.has(parseInt(id))) {
                            const urls = prev[id];
                            if (urls.photoUrl && urls.photoUrl.startsWith('blob:')) {
                                URL.revokeObjectURL(urls.photoUrl);
                            }
                            if (urls.videoUrl && urls.videoUrl.startsWith('blob:')) {
                                URL.revokeObjectURL(urls.videoUrl);
                            }
                        }
                    });

                    // Keep existing blob URLs for records still in filtered results, merge with new ones
                    const cleaned = Object.keys(prev)
                        .filter(id => currentRecordIds.has(parseInt(id)))
                        .reduce((acc, id) => {
                            acc[id] = prev[id];
                            return acc;
                        }, {});

                    return { ...cleaned, ...newBlobUrls };
                });
                setIsLoading(false);
            }
        };

        loadMedia();

        return () => {
            isMounted = false;
        };
    }, [records, fetchSecureMedia]);

    // Reset to page 1 when records change
    useEffect(() => {
        if (externalPage === null) {
            setInternalPage(1);
        }
    }, [records, externalPage]);

    // Cleanup blob URLs on unmount
    useEffect(() => {
        return () => {
            setMediaBlobUrls(prev => {
                Object.values(prev).forEach(urls => {
                    if (urls.photoUrl && urls.photoUrl.startsWith('blob:')) {
                        URL.revokeObjectURL(urls.photoUrl);
                    }
                    if (urls.videoUrl && urls.videoUrl.startsWith('blob:')) {
                        URL.revokeObjectURL(urls.videoUrl);
                    }
                });
                return {};
            });
        };
    }, []);
   
    const totalPages = records ? Math.ceil(records.length / RECORDS_PER_PAGE) : 0;

    return {
        mediaBlobUrls,
        isLoading,
        currentPage,
        totalPages,
        recordsPerPage: RECORDS_PER_PAGE
    };
};

